import { Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { generatePath, withRouter } from 'react-router-dom';
import styled from 'styled-components';
import getComponentName from 'react-utils/getComponentName';
import { NavMarker } from 'react-rhumb';
import memoize from 'transmute/memoize';
import { Set as ImmutableSet, Map as ImmutableMap } from 'immutable';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import uniqueIdForObject from 'automation-ui-core/utils/uniqueIdForObject';
import { setScrollToActionCardState, clearScrollToActionCardState } from 'automation-ui-canvas/utils/routingUtils';
import { parseLocation } from 'automation-ui-routing/utils';
import { wireAuthConsumer } from 'automation-ui-core/context/AuthContext';
import { getObjectTypeId, hydrateAction, hydrateAllActions, getActionOutputsForTargetActionById, assignIdsToActionClones, cloneHydratedActions } from 'automation-ui-canvas/flow/records/Flow/flowUtils';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import * as UsageTrackingScreens from 'automation-ui-usage-tracking/UsageTrackingScreens';
import { trackPageView, trackEvent } from 'automation-ui-canvas/utils/trackingUtils';
import { ScreenTrackingContextProvider } from 'automation-ui-usage-tracking/ScreenTrackingContext';
import * as flowActions from 'automation-ui-canvas/actions/FlowActions';
import PannableDecorator from 'automation-ui-canvas/decorators/PannableDecorator';
import ZoomableDecorator from 'automation-ui-canvas/decorators/ZoomableDecorator';
import { translateToGraph } from 'automation-ui-canvas/utils/platformWorkflowTranslators';
import { getLeafIds, getDescendentEdges } from 'automation-ui-directed-graph/DirectedNodes';
import MoveActionConfirmationPopover from 'automation-ui-canvas/components/canvas/MoveActionConfirmationPopover';
import InsertOrRemoveBranchModal from 'automation-ui-canvas/components/modals/InsertOrRemoveBranchModal';
import { attachWrappedComponent } from 'automation-ui-core/decoratorUtils';
import { getSourceApp } from 'automation-ui-canvas/selectors/embedSelectors';
import { getPreviousActionId } from 'automation-ui-action-features/records/recordsUtils';
import FlowCanvasNode from 'automation-ui-canvas/components/canvas/FlowCanvasNode';
import { ActionCardOptions } from 'automation-ui-canvas/components/action-card-options/constants/ActionCardOptions';
import { correctCyclesInFlowAfterMove } from 'automation-ui-canvas/utils/flowCanvasUtils';
import { queryObjectsInActions } from 'automation-ui-canvas/action-features/objects-in-action/ObjectsInActionQueries';
import { sumObjectsInActionsCount } from 'automation-ui-canvas/action-features/objects-in-action/objectsInActionUtils';
import { wireActionOutputs } from 'automation-ui-action-features/queries/actionOutputQueries';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { deleteClonedCustomCodeActions } from 'automation-ui-canvas/data/api/cloneActionApis';
import * as ActionTypes from 'automation-ui-action-features/constants/ActionTypes';
import { wireFlowById, wireUpdateFlow } from 'automation-ui-canvas/queries/flowQueries';
import { CANVAS_ROOT } from 'automation-ui-canvas/constants/AbsolutePaths';
import { getScreenForFlowType } from 'automation-ui-canvas/utils/getScreenForFlowType';
import { buildSearchStringForActionSetRoutes } from 'automation-ui-canvas/utils/buildSearchStringForActionSetRoutes';
import { getZoomLevel } from 'automation-ui-canvas/selectors/localStorageSelectors';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledCanvas = styled.div.withConfig({
  displayName: "FlowCanvasMoveActionDecorator__StyledCanvas",
  componentId: "sc-1e4alyb-0"
})(["height:100%;"]);
function getComposedCanvas() {
  return PannableDecorator(
  // TODO(TS_BUG)
  // @ts-expect-error ZoomableDecorator expects StyledCanvas to have its required props, but it's just a div
  // and the required props aren't wired until ComposedCanvas is used in FlowCanvasBase. Not sure if real error or not
  ZoomableDecorator({
    WrappedComponent: StyledCanvas
  }));
}
export default function FlowCanvasMoveActionDecorator(WrappedComponent, centerOnMount) {
  const componentName = getComponentName(WrappedComponent);
  class FlowCanvasMoveAction extends Component {
    constructor(...args) {
      super(...args);
      this.state = {
        errorKey: '',
        stagedChange: ImmutableMap(),
        edgeIdsForMove: ImmutableSet(),
        selectedActionIds: ImmutableSet(),
        isCloningCustomCodeAction: false,
        isLoading: false,
        selectedEdgeId: null,
        stagedClonedCustomCodeActionTypeIds: undefined
      };
      this.ComposedCanvas = getComposedCanvas();
      this.keydownEventListener = event => {
        const {
          key
        } = event;
        if (key === 'Escape') {
          this.redirectToFullCanvas();
        }
      };
      this.setEscapeKeyEventListener = () => {
        document.addEventListener('keydown', this.keydownEventListener);
      };
      this.handleGetActionOutputsForTargetActionById = (targetActionId, isNew) => getActionOutputsForTargetActionById({
        flow: this.props.flow,
        allOutputsByActionTypeId: this.props.allOutputsByActionTypeId,
        instanceOutputsByActionId: this.props.instanceOutputsByActionId,
        targetActionId,
        isNewAction: isNew
      });
      this.removeEscapeKeyEventListener = () => {
        document.removeEventListener('keydown', this.keydownEventListener);
      };
      this.getComposedProps = () => {
        const {
          canvasScrollElementRef,
          flow,
          zoomLevel
        } = this.props;
        return {
          flow: this.getFlowToRender(),
          onClickAddControl: this.onClickTargetZone,
          canvasScrollElementRef,
          onRenderCanvasNode: this.onRenderCanvasNode,
          onRenderConnectorButton: this.onRenderConnectorButton(this.state.isLoading),
          ComposedCanvas: this.ComposedCanvas,
          // EJM - override on action set only
          onRenderEndMarker: flow.isActionSet() ? () => null : undefined,
          centerOnMount,
          zoomLevel
        };
      };
      this.onRenderCanvasNode = memoize(({
        id: nodeId,
        width,
        height
      }) => {
        const {
          flow,
          cloneAction,
          moveAllChildren,
          objectsInActions,
          showEnrollmentCard,
          isMulti,
          zoomLevel
        } = this.props;
        const {
          errorKey,
          stagedChange,
          selectedActionIds
        } = this.state;
        const hasStagedChange = Boolean(stagedChange.get('actionId'));
        const isSelected = selectedActionIds.has(nodeId);
        if (nodeId === 'enrollment' && !showEnrollmentCard) {
          return null;
        }
        let content = /*#__PURE__*/_jsx(FlowCanvasNode, {
          id: nodeId,
          width: width,
          height: height,
          flow: flow,
          isSelected: false,
          isHighlighted: isSelected,
          isDisabled: !hasStagedChange && !isSelected,
          readOnly: true,
          showIcon: !isMulti,
          getActionOutputsForTargetActionById: this.handleGetActionOutputsForTargetActionById,
          objectsInActions: objectsInActions,
          zoomLevel: zoomLevel
        });
        const showConfirmationTooltip = hasStagedChange && nodeId === stagedChange.get('actionId');
        if (showConfirmationTooltip) {
          // delayed objects don't get carried over when cloning actions
          const delayEnrollmentCount = cloneAction ? 0 : this.getDelayEnrollmentCount(selectedActionIds);
          content = /*#__PURE__*/_jsx(MoveActionConfirmationPopover, {
            onSave: this.finalizeMove,
            onCancel: this.unstageChanges,
            cloneAction: cloneAction,
            moveAllChildren: moveAllChildren,
            delayEnrollmentCount: delayEnrollmentCount,
            changeRemovesGoToEdges: stagedChange.get('changeRemovesGoToEdges'),
            flowObjectTypeId: getObjectTypeId(flow),
            errorKey: errorKey,
            children: content
          });
        }
        return content;
      }, ({
        id: nodeId
      }) => {
        const objectsInThisAction = this.props.objectsInActions ? this.props.objectsInActions.find(delayedAction => delayedAction.actionId === nodeId) : null;
        const objectsInThisActionCount = objectsInThisAction && objectsInThisAction.count;
        return `${nodeId}-${uniqueIdForObject(this.props.flow)}-${this.props.flowId}-${this.props.actionId}-${this.state.edgeIdsForMove}-${uniqueIdForObject(this.state.stagedChange)}-${this.state.errorKey}-${objectsInThisActionCount}-${this.props.zoomLevel}`;
      });
      this.onRenderConnectorButton = isLoading => ({
        edge,
        readOnly
      }) => {
        const {
          edgeIdsForMove,
          isCloningCustomCodeAction,
          selectedEdgeId
        } = this.state;
        const edgeId = edge.getId();
        const showSpinner = isLoading && selectedEdgeId === edgeId;
        const showButton = edgeIdsForMove.has(edgeId) && !readOnly && edge.getShouldShowAddControl();
        return showButton ? /*#__PURE__*/_jsx(UITooltip, {
          disabled: !showSpinner && !isCloningCustomCodeAction,
          open: showSpinner && isCloningCustomCodeAction,
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "automation.moveActionCanvas.cloneCustomCodeActionLoadingLabel"
          }),
          children: /*#__PURE__*/_jsx(UILoadingButton, {
            use: "primary-white",
            onClick: () => this.onClickTargetZone(edgeId),
            selected: false,
            disabled: Boolean(this.state.stagedChange.get('actionId')),
            "data-test-id": `move-connector-${edgeId}`,
            loading: showSpinner,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "automation.common.editActionDropdown.targetZone"
            })
          })
        }) : null;
      };
      this.finalizeMove = () => {
        const {
          cloneAction,
          moveAllChildren,
          sourceApp,
          updateFlow
        } = this.props;
        const {
          stagedChange
        } = this.state;
        if (cloneAction) {
          trackEvent(Trackables.CLONED_ACTION, {
            allItems: moveAllChildren
          });
        } else {
          trackEvent(Trackables.MOVED_ACTION, {
            allItems: moveAllChildren
          });
        }
        updateFlow({
          flow: stagedChange.get('newFlow'),
          sourceApp
        }).then(res => {
          if (res) {
            this.redirectToFullCanvas();
          }
        }).catch(() => {
          this.setState({
            errorKey: 'automation.moveActionCanvas.confirmationPopover.saveError'
          }, this.forceCanvasRender);
        });
      };
      this.forceCanvasRender = () => {
        const {
          canvasScrollElementRef
        } = this.props;
        if (canvasScrollElementRef.current) {
          canvasScrollElementRef.current.scrollBy(1, 1);
          canvasScrollElementRef.current.scrollBy(-1, -1);
        }
      };
      this.unstageChanges = () => {
        const {
          revertStagedChanges,
          history
        } = this.props;
        const {
          stagedClonedCustomCodeActionTypeIds
        } = this.state;
        this.setState({
          errorKey: '',
          stagedChange: ImmutableMap(),
          isCloningCustomCodeAction: false,
          stagedClonedCustomCodeActionTypeIds: undefined
        }, () => {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          deleteClonedCustomCodeActions(stagedClonedCustomCodeActionTypeIds);
          revertStagedChanges();
          clearScrollToActionCardState(history);
          this.forceCanvasRender();
        });
      };
      this.cloneAndStageActions = (hydratedFlow, clonedActionIds, clonedActions, edgeId, stagedClonedCustomCodeActionTypeIds) => {
        const {
          actionId,
          moveAllChildren
        } = this.props;
        this.setState({
          isLoading: false,
          selectedEdgeId: null,
          stagedClonedCustomCodeActionTypeIds
        });
        const newActionId = clonedActionIds.get(actionId);
        const newFlow = hydratedFlow.cloneActions({
          actions: clonedActions,
          rootActionId: newActionId,
          edgeId,
          includeChildren: moveAllChildren
        });
        this.stageChanges({
          newFlow,
          newActionId
        });
      };
      this.onClickTargetZone = edgeId => {
        const {
          actionId,
          action,
          cloneAction,
          moveAllChildren,
          flow
        } = this.props;
        const {
          selectedActionIds
        } = this.state;
        if (cloneAction) {
          this.setState({
            isLoading: true,
            selectedEdgeId: edgeId
          });
          /**
           * Some actions are not hydrated when loading the flow.
           * Additional data must be fetched to clone these actions
           */
          const hydrationPromise = moveAllChildren ? hydrateAllActions({
            flow,
            actionId
          }) : hydrateAction({
            flow,
            actionId
          });

          // If cloning a branch action to a non-leaf node, show a modal
          // to select the branch to connect downstream actions
          if (action.isBranch() && !flow.isLeafNodeEdge(edgeId)) {
            this.setState({
              showInsertBranchModal: true,
              edgeIdToChange: edgeId,
              isLoading: false,
              selectedEdgeId: null
            });
          } else {
            this.setState({
              isCloningCustomCodeAction: true
            });
            const clonedActionIds = assignIdsToActionClones(flow, selectedActionIds);
            const clonedActionsPromise = hydrationPromise.then(hydratedFlow => cloneHydratedActions(hydratedFlow, clonedActionIds));
            Promise.all([hydrationPromise, clonedActionsPromise]).then(([hydratedFlow, clonedActions]) => {
              const stagedClonedCustomCodeActionTypeIds = clonedActions.filter(({
                actionType
              }) => actionType === ActionTypes.CUSTOM_CODE).map(({
                actionTypeId
              }) => actionTypeId);
              this.cloneAndStageActions(hydratedFlow, clonedActionIds, clonedActions, edgeId, stagedClonedCustomCodeActionTypeIds);
            }).catch(error => {
              console.error(`Cloning failed: ${error}`);
              this.setState({
                errorKey: 'automation.moveActionCanvas.confirmationPopover.saveError',
                isLoading: false,
                selectedEdgeId: null
              });
            });
          }
        } else {
          const newFlow = flow.moveAction({
            actionId,
            edgeId,
            moveAllChildren
          });
          this.stageChanges({
            newFlow,
            newActionId: actionId
          });
        }
      };
      this.redirectToFullCanvas = () => {
        const {
          flow,
          flowId,
          history,
          location,
          revertStagedChanges
        } = this.props;
        revertStagedChanges();
        const screen = getScreenForFlowType(flow.flowType);
        const search = buildSearchStringForActionSetRoutes(flow, location);
        return history.replace({
          pathname: generatePath(CANVAS_ROOT, {
            flowId,
            screen
          }),
          search
        });
      };
      this.stageClonedBranch = ({
        removeAllChildren,
        branchIndexToKeep
      }) => {
        const {
          flow,
          actionId
        } = this.props;
        const {
          edgeIdToChange,
          selectedActionIds
        } = this.state;
        const clonedActionIds = assignIdsToActionClones(flow, selectedActionIds);
        const newActionId = clonedActionIds.get(actionId);

        // eslint-disable-next-line promise/catch-or-return,@typescript-eslint/no-floating-promises
        cloneHydratedActions(flow, clonedActionIds).then(clonedActions => {
          const newFlow = flow.cloneActions({
            actions: clonedActions,
            rootActionId: newActionId,
            edgeId: edgeIdToChange,
            branchIndexToSetDownstreamActionsIn: branchIndexToKeep,
            deleteDownstreamActions: removeAllChildren,
            includeChildren: false
          });
          this.stageChanges({
            newFlow,
            newActionId
          });
          this.setState({
            showInsertBranchModal: false
          });
        });
      };
      this.closeCloneBranchModal = () => {
        this.setState({
          showInsertBranchModal: false
        });
      };
      this.renderCloneBranchConfirmation = () => {
        const {
          action,
          flow
        } = this.props;
        const connection = action.getConnection();
        const branches = connection.getBranches();
        return /*#__PURE__*/_jsx(InsertOrRemoveBranchModal, {
          action: action,
          hideDefaultBranch: action.getActionType() === ActionTypes.AB_TEST_BRANCH,
          getActionOutputsForTargetActionById: this.handleGetActionOutputsForTargetActionById,
          flowObjectTypeId: getObjectTypeId(flow),
          onSave: this.stageClonedBranch,
          onClose: this.closeCloneBranchModal,
          stagedChanges: {
            defaultBranchName: connection.getDefaultBranchName(),
            branches
          },
          langKeyPath: "edit.insertBranchModal"
        });
      };
    }
    componentDidMount() {
      this.setEscapeKeyEventListener();
      trackPageView(UsageTrackingScreens.PLATFORM_FLOW_MOVE_ACTION_CONTAINER);
      this.setValidEdgeIds();
    }
    componentDidUpdate(prevProps) {
      const {
        flowId,
        flow
      } = this.props;
      if (flowId !== prevProps.flowId) {
        FlowCanvasMoveAction.handleRequestFlow(this.props);
      } else if (flow !== prevProps.flow) {
        this.setValidEdgeIds();
      }
    }
    componentWillUnmount() {
      this.removeEscapeKeyEventListener();
    }
    /**
     * Determine legal locations to move a set of actions
     * Prevent moves from creating cycles in the graph
     * Action sets including branches can only be moved to leaf nodes
     */
    setValidEdgeIds() {
      const {
        action,
        actionId,
        cloneAction,
        flow,
        moveAllChildren
      } = this.props;
      let selectedActionIds = ImmutableSet([actionId]);
      if (!flow || !action) {
        this.setState({
          edgeIdsForMove: ImmutableSet(),
          selectedActionIds
        });
        return;
      }
      const flowAsGraph = translateToGraph(flow, false);
      const edges = flowAsGraph.getEdges();
      let edgeIds = edges.map(edge => edge.getId());
      const descendentEdges = getDescendentEdges(actionId, edges);
      if (moveAllChildren) {
        const leafEdgeIds = getLeafIds(edges);
        edgeIds = edgeIds.filter(id => leafEdgeIds.includes(id));
        const descendantActionIds = descendentEdges.filter(edge => !!edge.getToNode()).map(edge => edge.getToNode());
        selectedActionIds = selectedActionIds.union(descendantActionIds);
      }
      if (!cloneAction) {
        if (actionId === flow.getFirstActionId()) {
          edgeIds = edgeIds.filter(id => id !== flow.getEnrollmentEdge().edgeId);
        } else {
          const parentEdgeIds = flow.getParentEdges({
            actionId,
            includeGoToEdges: false
          }).map(edge => edge.edgeId);
          edgeIds = edgeIds.filter(id => !parentEdgeIds.includes(id));
        }
        edgeIds = edgeIds.filter(id => getPreviousActionId(id) !== actionId);
        if (moveAllChildren) {
          const descendentEdgeIds = descendentEdges.map(edge => edge.getId());
          edgeIds = edgeIds.filter(edgeId => !descendentEdgeIds.includes(edgeId));
        }
      }
      this.setState({
        edgeIdsForMove: ImmutableSet(edgeIds),
        selectedActionIds
      });
    }
    getDelayEnrollmentCount(actionIds) {
      const {
        objectsInActions
      } = this.props;
      if (!objectsInActions) {
        return 0;
      }
      return sumObjectsInActionsCount({
        actionIdsToSum: actionIds,
        objectsInActions
      });
    }

    /**
     * Due to how the virtual renderer in the UIDirectedGraph
     * operates, it requires a scroll event to ensure
     * the cached nodes are re-rendered.
     */

    /**
     * Stage a move/clone change for confirmation
     */
    stageChanges({
      newFlow,
      newActionId
    }) {
      const {
        updateFlowLocally,
        cloneAction,
        moveAllChildren,
        history
      } = this.props;
      let hasCycle = false;
      if (!cloneAction || moveAllChildren) {
        hasCycle = newFlow.hasCycle(newActionId);

        // if cycle detected, remove the goto edges causing it and pass the
        // changeRemovesGoToEdges flag so we can notify the user
        if (hasCycle) {
          newFlow = correctCyclesInFlowAfterMove(newFlow, newActionId);
        }
      }
      this.setState({
        stagedChange: ImmutableMap({
          actionId: newActionId,
          newFlow,
          changeRemovesGoToEdges: hasCycle
        })
      }, () => {
        updateFlowLocally(newFlow);
        setScrollToActionCardState(newActionId, history, 'instant');
        this.forceCanvasRender();
      });
    }

    /**
     * When a move/clone is cancelled or confirmed, reset the app state and the component state
     */

    /**
     * Clones and stages new actions for selected actions.
     */

    getFlowToRender() {
      const {
        flow
      } = this.props;
      const {
        stagedChange
      } = this.state;
      return stagedChange.get('newFlow') || flow;
    }
    render() {
      const {
        flow
      } = this.props;

      // TODO: handle error fetching flow and add to Raegan
      if (!flow) {
        return null;
      }
      return /*#__PURE__*/_jsx(NavMarker, {
        name: "MOVE_ACTION_CANVAS",
        children: /*#__PURE__*/_jsxs(ScreenTrackingContextProvider, {
          value: UsageTrackingScreens.PLATFORM_FLOW_MOVE_ACTION_CONTAINER,
          children: [/*#__PURE__*/_jsx(WrappedComponent, Object.assign({}, this.props, this.getComposedProps())), this.state.showInsertBranchModal && this.renderCloneBranchConfirmation()]
        })
      });
    }
  }
  FlowCanvasMoveAction.defaultProps = {
    canvasScrollElementRef: null
  };
  FlowCanvasMoveAction.handleRequestFlow = ({
    flowQuery,
    flowId
  }) => {
    if (flowId > 0) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      flowQuery.refetch({
        flowId
      });
    }
  };
  const ConnectedFlowCanvasMoveAction = compose(withRouter, wireAuthConsumer, _WrappedComponent => props => {
    const {
      flowId: stringFlowId
    } = props.match.params;
    const flowId = Number(stringFlowId);
    return /*#__PURE__*/_jsx(_WrappedComponent, Object.assign({}, props, {
      flowId: flowId
    }));
  }, WrappedComponentDataFetching => props => {
    return queryObjectsInActions(WrappedComponentDataFetching, props.flowId)(props);
  }, wireFlowById, connect((state, ownProps) => {
    const {
      flowId,
      flow
    } = ownProps;
    const {
      actionId: stringActionId
    } = ownProps.match.params;
    const parsedQuery = parseLocation(ownProps.location);
    const {
      operation,
      moveAllChildren
    } = parsedQuery;
    const actionId = Number(stringActionId);
    const action = flow && flow.getActionById(actionId);
    const showEnrollmentCard = !flow || !flow.isActionSet();
    return {
      flow,
      flowId,
      action,
      actionId,
      cloneAction: operation === ActionCardOptions.CLONE,
      moveAllChildren: moveAllChildren === 'true',
      sourceApp: getSourceApp(state),
      showEnrollmentCard,
      zoomLevel: getZoomLevel(flowId)(state)
    };
  }, {
    updateFlowLocally: flowActions.updateFlowLocally,
    revertStagedChanges: flowActions.revertStagedChanges
  }), wireUpdateFlow, _WrappedComponent => props => wireActionOutputs(_WrappedComponent, {
    flow: props.flow
  })(props))(FlowCanvasMoveAction);
  ConnectedFlowCanvasMoveAction.displayName = `ConnectedFlowCanvasMoveAction(${componentName})`;
  attachWrappedComponent(ConnectedFlowCanvasMoveAction, WrappedComponent);
  return ConnectedFlowCanvasMoveAction;
}